import { ref, computed } from 'vue'
import api from '@/services/api'

/**
 * Composable que carrega o menu a partir da API (GET /api/RegraAcesso/menu)
 * e converte a lista plana em árvore de 3 níveis no formato consumido pelo
 * MainDashboard e ModalMapaMenus.
 *
 * Fornece fallback silencioso: se a API falhar, retorna array vazio
 * e o sistema continua usando o menuConfig hardcoded do MainDashboard.
 *
 * Também expõe resolver(chaveRegra) para navegação externa via ?regra=CHAVE.
 */
export function useMenuBackend () {
  const menuFromApi = ref(null) // null = não carregado, [] = vazio, [...] = dados
  const isLoading = ref(false)
  const apiError = ref(false)

  /**
   * Converte a lista plana de RegraAcessoDto em árvore de 3 níveis.
   * Nível 1: IsGrupo=true → grupo
   * Nível 2: IsHeader=true → subseção (opcional)
   * Nível 3: !IsGrupo && !IsHeader → item de menu
   */
  function converterParaArvore (flatList) {
    const grupos = flatList.filter(r => r.isGrupo)
    const headers = flatList.filter(r => r.isHeader)
    const itens = flatList.filter(r => !r.isGrupo && !r.isHeader)

    return grupos.map(grupo => {
      // Busca subseções deste grupo
      const subsecoes = headers.filter(h => h.idRegraAcessoPai === grupo.idRegraAcesso)

      // Busca itens diretos do grupo (sem subseção)
      const itensDoGrupo = itens.filter(i => i.idRegraAcessoPai === grupo.idRegraAcesso)

      // Busca itens de cada subseção
      const todosItens = []
      for (const sub of subsecoes) {
        todosItens.push({ isHeader: true, title: sub.titulo || '' })
        const itensDaSub = itens.filter(i => i.idRegraAcessoPai === sub.idRegraAcesso)
        for (const item of itensDaSub) {
          todosItens.push(converterItem(item, grupo, sub))
        }
      }

      // Itens diretos do grupo (sem subseção)
      for (const item of itensDoGrupo) {
        todosItens.push(converterItem(item, grupo, null))
      }

      return {
        id: grupo.chave?.toLowerCase().replace(/_/g, '-') || `grupo-${grupo.idRegraAcesso}`,
        label: grupo.titulo || grupo.modulo,
        icon: grupo.icone || 'bi-circle',
        adminOnly: !grupo.isRegraParaUsuario,
        items: todosItens
      }
    })
  }

  function converterItem (item, grupo, subsecao) {
    let extraProps = {}
    if (item.extraPropsJson) {
      try {
        extraProps = JSON.parse(item.extraPropsJson)
      } catch {
        extraProps = {}
      }
    }

    // Extrai contextoNegocio/palavrasChave da primeira tela vinculada encontrada
    // nas permissoes[].telasVinculadas[] retornadas pelo endpoint /menu
    let contextoNegocio = ''
    let palavrasChave = ''
    if (item.permissoes && Array.isArray(item.permissoes)) {
      for (const permissao of item.permissoes) {
        if (permissao.telasVinculadas && Array.isArray(permissao.telasVinculadas)) {
          for (const tela of permissao.telasVinculadas) {
            if (tela.contextoNegocio || tela.palavrasChave) {
              contextoNegocio = tela.contextoNegocio || ''
              palavrasChave = tela.palavrasChave || ''
              break
            }
          }
          if (contextoNegocio || palavrasChave) break
        }
      }
    }

    return {
      module: item.telaAbertura || item.chave,
      title: item.titulo || item.chave,
      icon: item.icone || 'bi-circle',
      perm: item.chave,
      idRegraAcesso: item.idRegraAcesso,
      idDocumentacao: item.idDocumentacaoTelaDefault
        ? String(item.idDocumentacaoTelaDefault)
        : undefined,
      extraProps: Object.keys(extraProps).length > 0 ? extraProps : undefined,
      grupoLabel: grupo?.titulo || grupo?.modulo || '',
      subsecaoLabel: subsecao?.titulo || null,
      contextoNegocio,
      palavrasChave
    }
  }

  /**
   * Carrega o menu da API e converte para árvore.
   * Em caso de falha, menuFromApi fica como [] e apiError=true.
   */
  async function carregarMenu () {
    if (isLoading.value) return
    isLoading.value = true
    apiError.value = false

    try {
      const response = await api.get('/RegrasAcesso/menu', {
        _suppressLoading: true
      })
      const flatList = response.data || []
      menuFromApi.value = converterParaArvore(flatList)
    } catch (error) {
      console.warn('[useMenuBackend] Falha ao carregar menu da API, usando fallback:', error)
      menuFromApi.value = []
      apiError.value = true
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Dicionário plano para busca rápida por chave da regra.
   */
  const dicionarioPorChave = computed(() => {
    if (!menuFromApi.value) return {}
    const dict = {}
    for (const grupo of menuFromApi.value) {
      for (const item of grupo.items) {
        if (!item.isHeader && item.perm) {
          dict[item.perm] = {
            telaAbertura: item.module,
            titulo: item.title,
            extraProps: item.extraProps || {},
            permissoes: item.perm ? [item.perm] : [],
            grupoLabel: item.grupoLabel,
            subsecaoLabel: item.subsecaoLabel
          }
        }
      }
    }
    return dict
  })

  /**
   * Resolve uma chave de regra (ex: "EMBARQUES") para os dados de navegação.
   * Usado para navegação externa via ?regra=CHAVE.
   */
  function resolver (chaveRegra) {
    if (!chaveRegra) return null
    return dicionarioPorChave.value[chaveRegra] || null
  }

  return {
    menuFromApi,
    isLoading,
    apiError,
    carregarMenu,
    resolver
  }
}
